import React from "react";
import {
    IconDownArrowCard,
    IconStarCard
} from "../../../../../components/buttons/IconButtons";
import {POSITION_MODEL} from "../../../../../Helpers/PositionModel";
import CustomUserChip from "../../../../../components/CustomUserChip";
import CustomTooltip from "../../../../../components/CustomTooltip";
import {useTranslation} from "react-i18next";

/**
 * @email kpozos@ibl.mx
 * @author Karen
 * @title Full Stack Developer
 *
 * @component TotalMealCardContent
 * @description Displays a card with summarized information about meals per user.
 * It highlights the users with the maximum and minimum meal counts, and lists all users with their counts.
 *
 * @param {Object} props - Component props.
 * @param {string} props.courseType - Type of course/meal category (translated).
 * @param {Array} props.list - List of users with meal counts. Each item must contain:
 *   - {string} name: User's name.
 *   - {number} count: Number of meals assigned to the user.
 *
 * @returns {JSX.Element} Renders a set of cards with user names, counts, and icons to indicate min/max values.
 */
const TotalMealCardContent = ({ courseType, list }) => {
    const { t } = useTranslation();

    // If no data is provided, return empty string (no UI).
    if (list.length === 0) return '';

    // Extract only counts from the list.
    const counts = list.map(item => item.count);

    // Find the maximum and minimum meal counts.
    const maxCount = Math.max(...counts);
    const minCount = Math.min(...counts);

    // Get users who have the maximum count.
    const maxItems = list.filter(item => item.count === maxCount);

    // Get users who have the minimum count (ensuring not equal to max).
    const minItems = list.filter(item => item.count === minCount && item.count !== maxCount);

    // Classes for text and layout depending on whether we have max and min users.
    const classNameText = maxItems?.length > 0 && minItems.length > 0 ? "" : "flex-grow-1 minw-0";
    const truncateClass = maxItems?.length > 0 && minItems.length > 0 ? "text-ellipsis-card-chip" : "text-ellipsis-1-row";

    return (
        <>
            {/* Header with course type and chips for max/min */}
            <div className={"col-12 mb-3 px-0"}>
                <div className={"d-flex justify-content-between align-items-center"}>
                    <p className={"fs-14px ibl-font-w-600 mr-3"}>{t(courseType)}</p>

                    <div className={`d-flex justify-content-end align-items-center ${classNameText}`}>

                        {/* Show chip for max user */}
                        { maxItems?.[0] &&
                        <CustomUserChip
                            className={`chip-warning-150-100 ${minItems?.[0]?.name ? "my-0 ml-0 mr-2":"m-0"}`}
                            name={
                                <CustomTooltip
                                    text={ maxItems?.[0]?.name }
                                    classes={`fs-14px ${truncateClass}`}
                                    showIfTruncated={true}
                                />
                            }
                            classNameText={`d-flex align-items-center ${classNameText}`}
                            Icon={IconStarCard}
                            showAvatar={true}
                            classNameIcon={"icon-color-warning-700 h-18px"}
                            variant="compact"
                            iconPosition={POSITION_MODEL.LEFT}
                        />
                        }

                        {/* Show chip for min user */}
                        { minItems?.[0] &&
                        <CustomUserChip
                            className="m-0 chip-secondary-150-100-500"
                            name={
                                <CustomTooltip
                                    text={ minItems?.[0]?.name }
                                    classes={`fs-14px ${truncateClass}`}
                                    showIfTruncated={true}
                                />
                            }
                            classNameText={`d-flex align-items-center ${classNameText}`}
                            Icon={IconDownArrowCard}
                            showAvatar={true}
                            classNameIcon={"icon-color-secondary-500 h-16px"}
                            variant="compact"
                            iconPosition={POSITION_MODEL.LEFT}
                        />
                        }
                    </div>
                </div>
            </div>

            {/* List of all users with meal counts */}
            { list?.map((row, x) => {
                return (
                    <div key={x} className={"col-12 card-shades-100 border-radius-8px mb-3"}>
                        <div className={"d-flex justify-content-between align-items-center"}>
                            <p className={"fs-14px mr-4"}>{row.name}</p>
                            <div className={"d-flex align-items-center"}>
                                {/* Show star if user has max count */}
                                { row.count === maxCount &&
                                <div className={"d-flex align-items-center mr-2"}>
                                    <IconStarCard className="icon-color-warning-700" width={16} height={16}/>
                                </div>
                                }

                                {/* Show arrow if user has min count (not equal max) */}
                                { row.count === minCount && row.count !== maxCount &&
                                <div className={"d-flex align-items-center mr-2"}>
                                    <IconDownArrowCard className="icon-color-secondary-400" width={14} height={14}/>
                                </div>
                                }

                                {/* Always show the count */}
                                <p className={"fs-14px ibl-font-w-600"}>{row.count}</p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default TotalMealCardContent;
